import React from 'react';
import { Link } from '@mui/material';
import type { TweetFacet } from '../../services/mediaContracts';
import { toSafeHttpUrl } from '../../services/safeUrl';

export const richLinkSx = {
  color: '#1D9BF0',
  textDecoration: 'none',
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  cursor: 'pointer',
  display: 'inline',
  verticalAlign: 'baseline',
  '&:hover': { textDecoration: 'underline' },
} as const;

export type MentionClickHandler = (handle: string) => void;

const RESERVED_PATHS = new Set([
  'home',
  'search',
  'explore',
  'i',
  'settings',
  'messages',
  'notifications',
  'compose',
  'login',
  'signup',
  'intent',
  'hashtag',
  'status',
]);

/** Extract a bare handle from an x.com / twitter.com profile URL. */
export const profileHandleFromUrl = (raw: string): string | null => {
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (
      host !== 'x.com' &&
      host !== 'twitter.com' &&
      host !== 'mobile.twitter.com'
    ) {
      return null;
    }
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length !== 1) return null;
    const handle = parts[0].replace(/^@/, '');
    if (!/^\w{1,15}$/.test(handle)) return null;
    if (RESERVED_PATHS.has(handle.toLowerCase())) return null;
    return handle;
  } catch {
    return null;
  }
};

const MentionLink: React.FC<{
  handle: string;
  label: string;
  onMentionClick?: MentionClickHandler;
}> = ({ handle, label, onMentionClick }) => {
  const clean = handle.replace(/^@/, '');

  if (onMentionClick) {
    return (
      <Link
        component="button"
        type="button"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onMentionClick(clean);
        }}
        sx={richLinkSx}
      >
        {label}
      </Link>
    );
  }

  // Gallery always passes onMentionClick; keep same-tab fallback if not.
  return (
    <Link href={`https://x.com/${clean}`} sx={richLinkSx}>
      {label}
    </Link>
  );
};

const ExternalOrProfileLink: React.FC<{
  href: string;
  label: string;
  linkKey: string;
  onMentionClick?: MentionClickHandler;
}> = ({ href, label, linkKey, onMentionClick }) => {
  const safeHref = toSafeHttpUrl(href);
  if (!safeHref) {
    return <>{label}</>;
  }

  const profileHandle = profileHandleFromUrl(safeHref);
  if (profileHandle && onMentionClick) {
    return (
      <MentionLink
        handle={profileHandle}
        label={label.startsWith('@') ? label : `@${profileHandle}`}
        onMentionClick={onMentionClick}
      />
    );
  }

  return (
    <Link
      key={linkKey}
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      sx={richLinkSx}
    >
      {label}
    </Link>
  );
};

const linkifyPlainText = (
  chunk: string,
  onMentionClick?: MentionClickHandler,
  keyPrefix = 'p'
): React.ReactNode[] => {
  if (!chunk) return [];

  const regex = /(@\w{1,15})|(#\w+)|(https?:\/\/[^\s]+)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = regex.exec(chunk)) !== null) {
    if (match.index > last) {
      parts.push(chunk.slice(last, match.index));
    }

    const token = match[0];
    if (match[1]) {
      const handle = token.slice(1);
      parts.push(
        <MentionLink
          key={`${keyPrefix}-m-${i}`}
          handle={handle}
          label={token}
          onMentionClick={onMentionClick}
        />
      );
    } else if (match[2]) {
      const tag = token.slice(1);
      parts.push(
        <Link
          key={`${keyPrefix}-h-${i}`}
          href={`https://x.com/hashtag/${encodeURIComponent(tag)}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={richLinkSx}
        >
          {token}
        </Link>
      );
    } else {
      parts.push(
        <ExternalOrProfileLink
          key={`${keyPrefix}-u-${i}`}
          linkKey={`${keyPrefix}-u-${i}`}
          href={token}
          label={token}
          onMentionClick={onMentionClick}
        />
      );
    }

    last = match.index + token.length;
    i += 1;
  }

  if (last < chunk.length) {
    parts.push(chunk.slice(last));
  }

  return parts;
};

/** Render caption/bio text with clickable @mentions, #hashtags, and URLs. */
export const renderRichText = (
  text: string,
  options?: {
    facets?: TweetFacet[];
    onMentionClick?: MentionClickHandler;
  }
): React.ReactNode[] => {
  if (!text) return [];

  const onMentionClick = options?.onMentionClick;
  const sorted = [...(options?.facets || [])]
    .filter((f) => f.type !== 'media')
    .sort((a, b) => a.start - b.start);

  if (sorted.length === 0) {
    return linkifyPlainText(text, onMentionClick);
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  sorted.forEach((facet, i) => {
    if (facet.start > cursor) {
      nodes.push(
        <React.Fragment key={`t-${i}-pre`}>
          {linkifyPlainText(text.slice(cursor, facet.start), onMentionClick, `t${i}`)}
        </React.Fragment>
      );
    }

    const slice = text.slice(facet.start, facet.end);

    if (facet.type === 'mention') {
      const handle = (facet.original || slice.replace(/^@/, '')).replace(/^@/, '');
      nodes.push(
        <MentionLink
          key={`f-${i}`}
          handle={handle}
          label={`@${handle}`}
          onMentionClick={onMentionClick}
        />
      );
    } else if (facet.type === 'hashtag') {
      const tag = facet.original || slice.replace(/^#/, '');
      nodes.push(
        <Link
          key={`f-${i}`}
          href={`https://x.com/hashtag/${encodeURIComponent(tag)}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={richLinkSx}
        >
          {`#${tag}`}
        </Link>
      );
    } else if (facet.href) {
      const safeHref = toSafeHttpUrl(facet.href);
      const profileHandle = safeHref ? profileHandleFromUrl(safeHref) : null;
      if (!safeHref) {
        nodes.push(
          <React.Fragment key={`f-${i}`}>{facet.display || slice}</React.Fragment>
        );
      } else if (profileHandle && onMentionClick) {
        nodes.push(
          <MentionLink
            key={`f-${i}`}
            handle={profileHandle}
            label={
              facet.display?.startsWith('@')
                ? facet.display
                : `@${profileHandle}`
            }
            onMentionClick={onMentionClick}
          />
        );
      } else {
        nodes.push(
          <Link
            key={`f-${i}`}
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={richLinkSx}
          >
            {facet.display || slice}
          </Link>
        );
      }
    } else {
      nodes.push(<React.Fragment key={`f-${i}`}>{facet.display || slice}</React.Fragment>);
    }

    cursor = Math.max(cursor, facet.end);
  });

  if (cursor < text.length) {
    nodes.push(
      <React.Fragment key="tail">
        {linkifyPlainText(text.slice(cursor), onMentionClick, 'tail')}
      </React.Fragment>
    );
  }

  return nodes;
};
